import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faChevronLeft, faChevronRight, faInbox, faBullhorn } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../auth/AuthContext.js';
import StatusBadge from '../components/StatusBadge.js';
import { firstNameFrom } from './greeting.js';
import NoticeCopy from './NoticeCopy.js';
import { DEFAULT_NOTICE_PALETTE, paletteCssVars, resolveNoticePalette } from './palette.js';
import { normalizeSnoozeFor } from './useNoticeboard.js';
import '../common/modals.css';
import './NoticeboardModal.css';

const formatPublishedAt = (publishedAt) => {
  if (!publishedAt) return '';
  return new Date(publishedAt).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const BrandFooter = () => (
  <div className="noticeboard-brand-footer">
    <a
      href="https://medgenixtech.co.uk"
      target="_blank"
      rel="noopener noreferrer"
      className="noticeboard-brand"
    >
      <img src="/medgenix-tech.png" alt="" className="noticeboard-brand-logo" />
      <span>MedGenix Tech</span>
    </a>
  </div>
);

const NoticeboardModal = ({
  isOpen,
  stacked = false,
  visibleNotices,
  isDue,
  isComplete,
  getData,
  selectedNotice,
  onGotIt,
  onSave,
  onComplete,
  onSnooze,
  onPageChange,
  onClose
}) => {
  const { user } = useAuth() || {};
  const name = firstNameFrom(user?.name);

  const savedData = selectedNotice ? getData(selectedNotice.noticeId) : undefined;
  const Content = selectedNotice?.Content;
  const completed = selectedNotice ? isComplete(selectedNotice) : false;
  const snoozeOptions = selectedNotice ? normalizeSnoozeFor(selectedNotice) : [];
  const isPersistent = snoozeOptions.length > 0 && !completed;
  const canDismiss = !isPersistent;
  const showGotIt = Boolean(selectedNotice) && !Content && !isPersistent;
  const showSnooze = isPersistent && !Content;
  const isNew = selectedNotice ? isDue(selectedNotice) : false;
  const currentIndex = selectedNotice
    ? visibleNotices.findIndex(notice => notice.noticeId === selectedNotice.noticeId)
    : -1;
  const total = visibleNotices.length;
  const showPaginator = Boolean(selectedNotice) && total > 1 && currentIndex >= 0 && canDismiss;
  const palette = selectedNotice
    ? resolveNoticePalette(selectedNotice)
    : DEFAULT_NOTICE_PALETTE;

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (canDismiss && e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className={`modal-overlay${stacked ? ' noticeboard-overlay-stacked' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="noticeboard-modal-title"
      onClick={handleBackdropClick}
    >
      <div
        className="modal-container modal-animated noticeboard-modal"
        style={paletteCssVars(palette)}
      >
        <div className="modal-header noticeboard-campaign-header">
          <div className="noticeboard-header-title">
            <FontAwesomeIcon icon={faBullhorn} className="noticeboard-header-icon" />
            <h2 id="noticeboard-modal-title">Notices</h2>
          </div>
          {canDismiss && (
            <button type="button" className="close-button" onClick={onClose} aria-label="Close notices">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>

        {selectedNotice ? (
          <>
            <div className="modal-content noticeboard-detail-content">
              <div className="noticeboard-detail-meta">
                <h3 className="noticeboard-detail-title">{selectedNotice.title}</h3>
                <StatusBadge
                  label={isNew ? 'NEW' : 'VIEWED'}
                  colorFamily={isNew ? 'green' : 'gray'}
                />
                {selectedNotice.publishedAt && (
                  <span className="noticeboard-date">{formatPublishedAt(selectedNotice.publishedAt)}</span>
                )}
              </div>
              {Content ? (
                <Content
                  key={selectedNotice.noticeId}
                  notice={selectedNotice}
                  savedData={savedData}
                  name={name}
                  onSave={onSave}
                  onComplete={onComplete}
                  onSnooze={onSnooze}
                />
              ) : (
                <NoticeCopy text={selectedNotice.body} name={name} />
              )}
            </div>
            <BrandFooter />
            {(showPaginator || showGotIt || showSnooze) && (
              <div className="noticeboard-footer">
                {showPaginator && (
                  <NoticePaginator
                    currentIndex={currentIndex}
                    total={total}
                    onPageChange={onPageChange}
                  />
                )}
                {showSnooze && (
                  <div className="noticeboard-snooze" role="group" aria-label="Remind me later">
                    {snoozeOptions.map((option) => (
                      <button
                        key={option.id || option.ms}
                        type="button"
                        className="header-button"
                        onClick={() => onSnooze(option.ms)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
                {showGotIt && (
                  <button type="button" className="header-button header-button-primary" onClick={onGotIt}>
                    Got it
                  </button>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="modal-content noticeboard-detail-content">
              <div className="noticeboard-empty">
                <FontAwesomeIcon icon={faInbox} className="noticeboard-empty-icon" />
                <p className="noticeboard-empty-title">No notices right now</p>
                <p className="noticeboard-empty-subtitle">New announcements will appear here when they are published.</p>
              </div>
            </div>
            <BrandFooter />
          </>
        )}
      </div>
    </div>
  );
};

const NoticePaginator = ({ currentIndex, total, onPageChange }) => {
  const page = currentIndex + 1;
  return (
    <div className="noticeboard-pagination" role="navigation" aria-label="Notices">
      <button
        type="button"
        className="noticeboard-pagination-button"
        onClick={() => onPageChange(currentIndex - 1)}
        disabled={currentIndex <= 0}
      >
        <FontAwesomeIcon icon={faChevronLeft} />
        Previous
      </button>
      <div className="noticeboard-pagination-pages">
        {Array.from({ length: total }, (_, index) => (
          <button
            key={index}
            type="button"
            className={`noticeboard-pagination-page${index === currentIndex ? ' active' : ''}`}
            onClick={() => onPageChange(index)}
            aria-current={index === currentIndex ? 'page' : undefined}
          >
            {index + 1}
          </button>
        ))}
      </div>
      <button
        type="button"
        className="noticeboard-pagination-button"
        onClick={() => onPageChange(currentIndex + 1)}
        disabled={currentIndex >= total - 1}
      >
        Next
        <FontAwesomeIcon icon={faChevronRight} />
      </button>
      <span className="noticeboard-pagination-summary">{page} of {total}</span>
    </div>
  );
};

export default NoticeboardModal;
